// ----------------------------------------------------------------------
// <copyright file="Izenda.QuerySourceTypeDetailsStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { getOr, find, filter, uniqueId, some, map, reduce, forEach, pick, chain, join, compact } from 'lodash/fp';
import { observable, action } from 'mobx';
import tracker, { dirty } from 'share/tracking/Izenda.Tracker';

import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import PagingModel from 'share/components/controls/DataGrid/helpers/Izenda.PagingModel';
import CalculatedFieldStore from 'logic/Izenda.CalculatedFieldStore';

import fetcher from 'fetchers/Izenda.DataModelDataFetcher';
import reportFetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import uiState from 'logic/Izenda.UiState';
import QuerySourceFieldDetailsStore from './Izenda.QuerySourceFieldDetailsStore';
import constants from 'logic/data/Izenda.DataModelConstants';
import { FILTER_LOOKUP_STATUSES } from 'logic/data/Izenda.FilterLookupConstants';
import { computed } from '../../node_modules/mobx/lib/mobx';
import PredictedFieldStore from 'logic/Izenda.PredictedFieldStore';
import MachineLearningStore from 'logic/Izenda.MachineLearningStore';
import MLSettings from 'logic/models/Izenda.MLSettings'

@tracker
export default class QuerySourceTypeDetailsStore {

  isDirty = false;

  colSortDir = {
    ColumnName: 'DESC'
  };

  @observable
  details = null;

  @observable
  aliasError = '';

  @observable
  executeErrorMessage = '';

  @observable
  isSelected = false;

  @observable
  predictedFieldStore = new PredictedFieldStore()

  @observable
  MachineLearningStore  =  new MachineLearningStore()



  @observable
  fieldList = [];

  @observable
  pageInfo = new PagingModel(this.colSortDir);

  @observable
  editingQuerySourceField = null;

  @observable
  isEditingFilter = false;

  @observable
  calculatedStore = new CalculatedFieldStore();

  constructor(details) {
    this.details = details || {};
    this.details.extendedProperties = JSON.parse(this.details.extendedProperties || '{}');
  }

  @computed
  get hasDetailChange() {
    return some(['isDirty', true], this.fieldList);
  }

  /**
   * Not deleted field list
   */
  @computed
  get displayFieldList() {
    return filter(field => field && field.getState() !== ObjectStateConstants.DELETE, this.fieldList);
  }

  /*
   * Get list of params from list of schema details
   */
  getParams() {
    return map(item => {
      const returnObject = pick(['id', 'name', 'filteredValue'], item.details);
      returnObject.filteredValue = JSON.stringify(returnObject.filteredValue);
      return returnObject;
    }, filter({ isParameter: true }, this.fieldList));
  }

  getDetails() {
    return this.details;
  }

  getFieldLists() {
    return this.fieldList;
  }

  get() {
    return this.fieldList;
  }

  getId() {
    return getOr(null, 'details.id', this);
  }

  getCategory() {
    return getOr(null, 'details.dataSourceCategoryId', this) || getOr(null, 'details.dataSourceCategoryName', this);
  }

  getDbName() {
    return getOr(null, 'details.connectionName', this);
  }

  getDbSourceName() {
    return getOr(null, 'details.name', this);
  }

  getDbSourceAlias() {
    return getOr(null, 'details.alias', this);
  }

  getFieldLevel() {
    return getOr(null, 'details.extendedProperties.FieldLevel', this);
  }

  getReturnedValue() {
    return getOr(null, 'details.extendedProperties.ReturnedValue', this);
  }

  getInputParams() {
    return getOr(null, 'details.extendedProperties.InputParams', this);
  }

  getExpressionLevel() {
    return getOr(null, 'details.extendedProperties.ExpressionLevel', this);
  }

  isDynamic() {
    return getOr(null, 'details.extendedProperties.Dynamic', this);
  }

  isStatic() {
    return getOr(null, 'details.extendedProperties.Static', this);
  }

  getQuerySourceDetailsType() {
    return getOr(null, 'details.extendedProperties.storedProcType', this);
  }

  @dirty
  setCategory(id, name, isNew) {
    const details = this.getDetails();
    details.dataSourceCategoryId = id;
    details.dataSourceCategoryName = name;
    details.isNewCategory = isNew;
  }

  @dirty
  setDbSourceAlias(alias) {
    // reset value of aliasError
    this.aliasError && (this.aliasError = '');
    this.details.alias = alias;
  }

  @dirty
  setFieldLevel(isFieldLevel) {
    this.details.extendedProperties.FieldLevel = isFieldLevel;
  }

  @dirty
  setExpressionLevel(isExpressionLevel) {
    this.details.extendedProperties.ExpressionLevel = isExpressionLevel;
  }

  @dirty
  setDynamic(isDynamic) {
    this.details.extendedProperties.Dynamic = isDynamic;
  }

  @action
  loadQuerySourceFields() {
    const data = Object.assign({}, this.details);
    data.extendedProperties = JSON.stringify(data.extendedProperties);
    this.pageInfo.buildPagingRequest();

    const sortOrders = this.pageInfo.sortOrders;
    return fetcher
      .loadQuerySourceFields(data, sortOrders, null, uiState.getTenantId())
      .then(this.populateData.bind(this));
  }

  /**
   * Partially restore user data.
   * @param {Object} fieldUserData preserved user data to restore it
   */
  restoreUserData(fieldUserData) {
    forEach(field => {
      const userField = find(f => f.details.id === field.details.id, fieldUserData);
      if (userField) {
        if (field.filterableChanged || field.visibleChanged || field.details.alias !== userField.details.alias) {
          field.markDirty();
        }
        field.filterableChanged = userField.filterableChanged;
        field.visibleChanged = userField.visibleChanged;
        field.details.filterable = userField.details.filterable;
        field.details.visible = userField.details.visible;
        field.details.alias = userField.details.alias;
      }
    }, this.fieldList);
  }

  @action
  populateData(querySourceTypeDetails, preserveUserData = false) {
    const preservedData = preserveUserData
      ? map(f => pick(['filterableChanged', 'visibleChanged', 'details'], f), this.fieldList)
      : [];

    this.querySourceTypeDetails = querySourceTypeDetails || {};
    const fieldListRaw = this.querySourceTypeDetails.result || [];

    const fieldList = map(fieldDetail => {
      const isDelete = fieldDetail.approval < constants.APPROVAL_LEVEL.QUERY_SOURCE_FIELD
        && fieldDetail.physicalChange === constants.DETECTED_CHANGE_LEVEL.DELETED;
      if (isDelete) {
        fieldDetail.visible = false;
        fieldDetail.filterable = false;
      }
      return new QuerySourceFieldDetailsStore(fieldDetail);
    }, fieldListRaw);
    // Allow save when have physical change
    if (some(['physicalChange', 1], fieldListRaw))
      this.markDirty();
    this.fieldList = fieldList;

    // restore preserved field's data
    if (preserveUserData)
      this.restoreUserData(preservedData);

    return this.fieldList;
  }

  /**
   * Delete field , only calculted field can be deleted
   * @param {Object} deletedField
   */
  @action
  async deleteField(deletedField) {
    const isGuid = String.isGuidFormat(deletedField.details.id);
    const deletePromise = isGuid
      ? fetcher.deleteQuerySourceField(deletedField.details.id)
      : Promise.resolve(true);
    const status = await deletePromise;
    if (status) {
      deletedField.setState(ObjectStateConstants.DELETE);
    }
    return status;
  }

  /**
   * Check if the calculted field is used in existing report
   * @param {Object} deletedField
   */
  hasReportUseCalculatedField(deletedField) {
    if (!deletedField.details.id) return Promise.resolve(false);

    return reportFetcher.hasReportUseCalculatedField(deletedField.details.id);
  }


  /**
 * Check if the calculted field is used in existing report
 * @param {Object} deletedField
 */
  hasReportUsePredicatedField(deletedField) {
    if (!deletedField.details.id) return Promise.resolve(false);

    return reportFetcher.hasReportUsePredicatedField(deletedField.details.id);
  }

  loadFieldNameForExpression(fieldDetails) {
    const details = fieldDetails && fieldDetails.details;
    
    const items = chain(this.fieldList)

      .flatMap(item => item.details)
      .filter(item => {
        
        // Do not get deleted item
        if (
          item.state === ObjectStateConstants.DELETE ||
          item.physicalChange === constants.DETECTED_CHANGE_LEVEL.DELETED
        ) {
          return false;
        }

        // Do not get current editing item
        // - get if current editing is undefined
        if (!details || (item.id && item.state !== ObjectStateConstants.UPDATE)) {
          return true;
        }

        // - filter temp item
        if (details.tempId !== undefined && item.tempId !== undefined) {
          return item.tempId !== details.tempId;
        }
       

        // - filter real item
        return details.id !== item.id;
      
      })
      .map(item => {
        return {
          id: item.id,
          name: item.alias || item.name,
          dataType: item.dataType || item.izendaDataType,
          description: item.expression,
          tempId: item.tempId,
          isPredicated:item.isPredicated
        };
      })
      .value();

    
    
    this.calculatedStore.expressionSelection.expressionFieldName.fetchFieldNames(items.filter(x=>!x.isPredicated));
  }

  /*
   * post execute store procedure
   */
  @dirty
  executeStoreProc() {
    const parseErrorMessage = messages =>
      chain(messages)
        .compact()
        .map(x => x.message)
        .compact()
        .join(', ')
        .value();

    const { sortOrders } = this.pageInfo;
    this.pageInfo.buildPagingRequest();
    fetcher
      .executeStoredProc(this.getId(), this.getParams(), sortOrders)
      .then(result => {
        this.executeErrorMessage = result.success ? '' : parseErrorMessage(result.messages);
        return result.data;
      })
      .then(data => this.populateData(data, true));
  }

  /**
   * Add calculated field
   * TODO : Need refactor : move to model
   * @param {type} calculatedData
   */
  @action
  @dirty
  addCalculateField(calculatedData) {
    const newFieldDetails = {
      alias: '',
      visible: true,
      filterable: !(calculatedData.isRunningField || calculatedData.isCompositeField),
      isCalculated: true,
      isPredicated: false,
      izendaDataType: calculatedData.izendaDataType,
      name: calculatedData.name,
      expression: calculatedData.expression,
      id: calculatedData.id,
      tempId: calculatedData.tempId,
      isRunningField: calculatedData.isRunningField,
      isCompositeField: calculatedData.isCompositeField,
      filterLookupStatus: FILTER_LOOKUP_STATUSES.VALID
    };

    // Add mode
    if (!newFieldDetails.id && !newFieldDetails.tempId) {

      newFieldDetails.tempId = uniqueId();
      newFieldDetails.position = this.fieldList.length;

      const field = new QuerySourceFieldDetailsStore(newFieldDetails);
      field.setState(ObjectStateConstants.INSERT);
      field.markDirty();
      this.fieldList.push(field);

    } else {

      const editedField = find(function (f) {
        return (
          (newFieldDetails.id && f.details.id === newFieldDetails.id) || (newFieldDetails.tempId && f.details.tempId === newFieldDetails.tempId)
        );
      }, this.fieldList);
      if (editedField) {
        const editedFieldDetails = editedField.getDetails();
        editedFieldDetails.izendaDataType = calculatedData.izendaDataType;
        editedFieldDetails.name = calculatedData.name;
        editedFieldDetails.expression = calculatedData.expression;
        editedFieldDetails.isRunningField = calculatedData.isRunningField;
        editedFieldDetails.isCompositeField = calculatedData.isCompositeField;
        editedFieldDetails.filterable = !(calculatedData.isRunningField || calculatedData.isCompositeField);
        // Update state to EDIT for existing calculated field
        if (editedFieldDetails.id)
          editedField.setState(ObjectStateConstants.UPDATE);
        editedField.markDirty();
      }
    }
    this.loadFieldNameForExpression();
  }

  @action
  @dirty
  addPredictableField(predictableData) {
    predictableData.inputFeatures.push(predictableData.relationColumn);
    var newField = {
      alias: '',
      id: predictableData.id,
      filterable: !(predictableData.isRunningField || predictableData.isCompositeField),
      isPredicated: true, // PREDICTABLE FLAG
      isCalculated: false,
      visible: true,
      tempId: predictableData.tempId,
      izendaDataType: (predictableData.selectedModelType === 'predict') ? 'Numeric' : 'Text', // predictableData.XXX: predictable field type from modal
      name: predictableData.name,
      isRunningField: predictableData.isRunningField,
      isCompositeField: predictableData.isCompositeField,
      inputFeatures: predictableData.inputFeatures,
      modelName: predictableData.selectedModel,
      targetValue: predictableData.targetValue,
      relationColumn: predictableData.relationColumn,
      accuracy: predictableData.accuracy,
      modelType: predictableData.modelType
    };


    // Add mode
    if (!newField.id && !newField.tempId) {
      newField.tempId = uniqueId();
      newField.state = ObjectStateConstants.INSERT;
      newField.position = this.fieldList.length;
      var field = new QuerySourceFieldDetailsStore(newField);
      field.details.predictableData = predictableData.extendedProperties;
      field.markDirty();
      this.fieldList.push(field);
    } else {
      var editedField = find(function (o) {
        return (
          (newField.id && o.details.id === newField.id) || (newField.tempId && o.details.tempId === newField.tempId)
        );
      }, this.fieldList);
      if (editedField) {
        editedField.details.izendaDataType = predictableData.izendaDataType;
        editedField.details.inputFeatures = predictableData.inputFeatures,
          editedField.details.modelName = predictableData.selectedModel,
          editedField.details.targetValue = predictableData.targetValue,
          editedField.details.relationColumn = predictableData.relationColumn,
          editedField.details.name = predictableData.name;
        editedField.details.accuracy = predictableData.accuracy;
        editedField.details.modelType = predictableData.modelType,
          editedField.details.accuracy = predictableData.accuracy;
        editedField.details.predictData = predictableData.predictData;
        editedField.details.isRunningField = predictableData.isRunningField;
        editedField.details.isCompositeField = predictableData.isCompositeField;
        editedField.details.filterable = !(predictableData.isRunningField || predictableData.isCompositeField);
        editedField.details.predictableData = predictableData.extendedProperties;
        // Update state = EDIT for existing predictable field
        if (editedField.details.id) {
          editedField.details.state = ObjectStateConstants.UPDATE;
        }
        editedField.markDirty();
      }
    }
    this.loadFieldNameForExpression();
  }

  resetCalculatedStore() {
    this.calculatedStore = new CalculatedFieldStore();
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.QuerySourceTypeDetailsStore.js